import {ActionMenu, ActionList, Tooltip} from '@primer/react'
import type {GraphqlFragment, IssueFormElementRef, SharedElementProps} from '../types'
import {ElementWrapper} from './ElementWrapper'
import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import {prefixMarkdownTitle} from '../utils/markdown-conversion'
import {graphql, useFragment} from 'react-relay'
import type {DropdownElement_input$data, DropdownElement_input$key} from './__generated__/DropdownElement_input.graphql'
import {VALIDATORS} from '@github-ui/entity-validators'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {uniqueIdForElementInternal} from '../utils/session-storage'
import styles from './DropdownElement.module.css'
import {safeGetDefaultValue} from '../utils/default-value'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const DEFAULT_CHOICE = 'None'

export type DropdownElementInternalProps = GraphqlFragment<DropdownElement_input$data> & {
  type: 'dropdown'
  index?: number
}

export const DropdownElement = forwardRef(
  (
    {
      elementRef,
      index,
      sessionStorageKey,
      defaultValuesById,
    }: {elementRef: DropdownElement_input$key; index?: number} & SharedElementProps,
    ref: ForwardedRef<IssueFormElementRef>,
  ) => {
    const data = useFragment(
      graphql`
        fragment DropdownElement_input on IssueFormElementDropdown {
          __id
          itemId: id
          label
          descriptionHTML
          options
          required
          multiple
          defaultOptionIndex: default
        }
      `,
      elementRef,
    )

    if (!data.options) return null
    const elementStorageKey = uniqueIdForElementInternal(sessionStorageKey, '', data.label, data.__id)
    return (
      <DropdownElementInternal
        {...data}
        index={index}
        ref={ref}
        type={'dropdown'}
        sessionStorageKey={elementStorageKey}
        defaultValuesById={defaultValuesById}
      />
    )
  },
)

export const DropdownElementInternal = forwardRef(
  (
    {
      itemId,
      index: indexOfFormElement,
      sessionStorageKey,
      label,
      options,
      descriptionHTML,
      required,
      multiple,
      defaultOptionIndex,
      defaultValuesById,
    }: DropdownElementInternalProps & SharedElementProps,
    ref: ForwardedRef<IssueFormElementRef>,
  ) => {
    const anchorRef = useRef<HTMLButtonElement>(null)
    const buttonTextRef = useRef<HTMLSpanElement>(null)
    const hasDefaultValue = defaultOptionIndex !== undefined && defaultOptionIndex !== null
    // We want to prefix the default `None` option if it's singular, not required, and doesn't have a default option.
    const optionChoices = useMemo(
      () => (multiple || required || hasDefaultValue ? options : [DEFAULT_CHOICE, ...options]),
      [hasDefaultValue, multiple, options, required],
    )

    // use the provided value in defaultValuesById. If no default value and dropdown is a single select,
    // choose the first option as the default
    const defaultSelectionValue = useMemo(() => {
      // extract default values from defaultValuesById. Cross-reference with optionChoices to ensure valid choices.
      let defaultSelection = safeGetDefaultValue({id: itemId, indexOfFormElement, value: '', defaultValuesById})
        .split(',')
        .map(str => str.trim())
        .filter(choice => optionChoices.includes(choice))

      // if there were no defaults in defaultValuesById, but defaultOptionIndex was provided, use that
      if (
        defaultSelection.length === 0 &&
        hasDefaultValue &&
        defaultOptionIndex >= 0 &&
        defaultOptionIndex < optionChoices.length
      ) {
        return [optionChoices[defaultOptionIndex]!]
      }

      // if no default provided and single select, choose the first option:
      if (defaultSelection.length === 0 && optionChoices.length > 0 && !multiple) {
        defaultSelection = [optionChoices[0]!]
      }

      return defaultSelection
    }, [defaultOptionIndex, defaultValuesById, hasDefaultValue, indexOfFormElement, itemId, multiple, optionChoices])

    const [selectedItems, setSelectedItems] = useSessionStorage<string[]>(sessionStorageKey, defaultSelectionValue)
    const [userHasChangedContent, setUserHasChangedContent] = useState(
      JSON.stringify(selectedItems) !== JSON.stringify(defaultSelectionValue),
    )

    // When rendering the page, it's possible for defaultValuesById to be populated on a re-render
    // rather than the original render. If this happens, the selectedItems and session storage will already
    // be set, so we need to manually overwrite them. We only do this if the user hasn't made changes
    // (they could set it back to the defaultOptionIndex or clear it, and we don't want to overwrite that).
    useEffect(() => {
      if (
        isFeatureEnabled('copilot_immersive_draft_issue_template_form_ui') &&
        JSON.stringify(selectedItems) !== JSON.stringify(defaultSelectionValue) &&
        !userHasChangedContent
      ) {
        setSelectedItems(defaultSelectionValue)
      }
    }, [defaultSelectionValue, selectedItems, setSelectedItems, userHasChangedContent])

    const [validationResult, setValidationResult] = useState<string | undefined>(undefined)

    const toggleSelection = useCallback(
      (option: string) => () => {
        if (selectedItems.includes(option)) {
          // We only allow deselection of the active item if we have multiple select.
          if (multiple) {
            setSelectedItems(selectedItems.filter(item => item !== option))
            setUserHasChangedContent(true)
          }
        } else {
          if (multiple) {
            setSelectedItems([...selectedItems, option])
          } else {
            setSelectedItems([option])
          }
          setUserHasChangedContent(true)
        }
      },
      [multiple, selectedItems, setSelectedItems],
    )

    useImperativeHandle(
      ref,
      () => ({
        focus: () => anchorRef.current?.focus(),
        markdown: () => prefixMarkdownTitle(label, selectedItems.join(', ')),
        validate: () => {
          if (required && selectedItems.length === 0) {
            setValidationResult(VALIDATORS.missingDropdownSelection)
            return false
          }
          return true
        },
        reset: () => {
          setSelectedItems(defaultSelectionValue)
          setValidationResult(undefined)
        },
        hasChanges: () => {
          if (selectedItems.length !== defaultSelectionValue.length) {
            return true
          }

          for (const item of selectedItems) {
            if (!defaultSelectionValue.includes(item)) {
              return true
            }
          }

          return false
        },
        getSessionStorageKey: () => sessionStorageKey,
      }),
      [defaultSelectionValue, label, required, selectedItems, sessionStorageKey, setSelectedItems],
    )

    useEffect(() => {
      if (selectedItems.length > 0) {
        setValidationResult(undefined)
      }
    }, [selectedItems])

    const [isTruncated, setIsTruncated] = useState(false)

    const buttonText = selectedItems.length === 0 ? 'None' : selectedItems.join(', ')

    // Only show tooltip if the text is truncated.
    // This is not bullet proof, since the true inner width of the element is rounded down.
    // I think it's pretty good though. See discussion here https://stackoverflow.com/questions/64689074/how-to-check-if-text-is-truncated-by-css-using-javascript
    useEffect(() => {
      if (!buttonTextRef.current) return
      const truncate = buttonTextRef.current.scrollWidth > buttonTextRef.current.clientWidth
      setIsTruncated(truncate)
    }, [buttonTextRef, setIsTruncated])

    return (
      <ElementWrapper
        label={label}
        description={descriptionHTML}
        required={required}
        validationResult={validationResult}
        sx={{alignItems: 'start'}}
      >
        {({labelId, descriptionIds}) => (
          <ActionMenu anchorRef={anchorRef}>
            {isTruncated ? (
              <Tooltip text={buttonText}>
                <ActionMenu.Button aria-labelledby={labelId} aria-describedby={descriptionIds}>
                  <span ref={buttonTextRef} className={styles.dropdownButtonText}>
                    {buttonText}
                  </span>
                </ActionMenu.Button>
              </Tooltip>
            ) : (
              <ActionMenu.Button aria-labelledby={labelId} aria-describedby={descriptionIds}>
                <span ref={buttonTextRef} className={styles.dropdownButtonText}>
                  {buttonText}
                </span>
              </ActionMenu.Button>
            )}
            <ActionMenu.Overlay width="medium" side="outside-top" maxHeight="medium" sx={{overflow: 'scroll'}}>
              <ActionList selectionVariant={multiple ? 'multiple' : 'single'}>
                {optionChoices.map((option, index) => (
                  <ActionList.Item
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    key={index}
                    selected={selectedItems.includes(option)}
                    onSelect={toggleSelection(option)}
                  >
                    {option}
                  </ActionList.Item>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
      </ElementWrapper>
    )
  },
)

DropdownElement.displayName = 'DropdownElement'
DropdownElementInternal.displayName = 'DropdownElementInternal'
